import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getDocumentById } from '../../../services/documentService';
import { VerifiedDocument } from '../../../types/documentTypes';

const styles: Record<string, React.CSSProperties> = {
  container: {
    minHeight: '100vh',
    background: '#f3f4f8',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: `'Segoe UI', Roboto, sans-serif`,
    padding: 24,
  },
  card: {
    background: '#ffffff',
    borderRadius: 16,
    boxShadow: '0 6px 20px rgba(0,0,0,0.08)',
    padding: '40px 32px',
    maxWidth: 550,
    width: '100%',
    textAlign: 'center',
    border: '1px solid #e5e7eb',
  },
  heading: {
    fontSize: 22,
    fontWeight: 600,
    color: '#1a1a1a',
    marginBottom: 20,
  },
  meta: {
    fontSize: 15,
    marginBottom: 10,
    color: '#444',
  },
  preview: {
    margin: '24px 0',
    padding: 12,
    background: '#f9fafb',
    borderRadius: 10,
    border: '1px solid #ddd',
    overflow: 'hidden',
  },
  error: {
    color: '#900',
    background: '#fff1f2',
    border: '1px solid #f8d7da',
    borderRadius: 10,
    padding: 24,
    fontSize: 16,
    fontWeight: 500,
  },
};

export default function VerifyDocumentView() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [doc, setDoc] = useState<VerifiedDocument | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getDocumentById(id)
      .then((res: any) => setDoc(res.data))
      .catch(() => setDoc({ valid: false }))
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>{t('document.verify.loading')}</div>
      </div>
    );
  }

  if (!doc?.valid) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.error}>
            {t('document.verify.invalid_link')}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <h2 style={styles.heading}>{t('document.verify.verified_title')}</h2>
        <div style={styles.preview}>
          {doc.fileUrl ? (
            <embed
              src={doc.fileUrl}
              type="application/pdf"
              width="100%"
              height="500px"
              style={{ border: 'none', borderRadius: 8 }}
            />
          ) : (
            <span>{t('document.verify.preview_unavailable')}</span>
          )}
        </div>

        {doc.fileUrl && (
          <a
            href={doc.fileUrl}
            target="_blank"
            rel="noopener noreferrer"
            download={doc.fileName}
            style={styles.link}
            className='btn btn-primary'
          >
            {t('document.verify.download')}
          </a>
        )}
      </div>
    </div>
  );
}
